import type { Employee, Store } from '../../types';

/**
 * Ποιος βλέπει τι στο «Ταμείο» (μενού χρήστη → ένα παράθυρο, απόφαση ιδιοκτήτη 6/10/2026). Οι κανόνες
 * είναι ΑΚΡΙΒΩΣ όσοι ίσχυαν για τις τέσσερις παλιές επιλογές του μενού:
 *  - Χ αναφορά: όλοι, εκτός αν αφαιρεθεί ρητά από τον υπάλληλο (register_x_report = false) — ίδιο με
 *    τον server (canPullXReport), που ελέγχει και την επανεκτύπωση.
 *  - Κλείσιμο ταμείου: διαχειριστής / manager / ρητό δικαίωμα close_register — ίδιο με τον server
 *    (canCloseRegister). Σε ΚΑΘΕ κατάστημα: από 12/10/2026 όλα κλείνουν χειροκίνητα (migration 266),
 *    το eod_mode δεν κρύβει πια το κουμπί.
 *  - Πληρωμές προσωπικού: διαχειριστής, ή όποιος δεν έχει αφαιρεμένο το dine_in_view_registers.
 *  - Κεράσματα: κατάστημα με τραπέζια, ίδιο δικαίωμα με τη Χ.
 *  - Προκαταβολές (12/10/2026): μόνο με ανοιχτό τον διακόπτη counter_deposits_enabled του
 *    καταστήματος (διαβάζεται ασύγχρονα — withDepositsTab) και δικαίωμα είσπραξης
 *    (dine_in_process_payment, ίδιο με τον server).
 *  - Κινήσεις ταμείου (19/10/2026, migration 277): με ανοιχτό τον διακόπτη cash_movements_enabled, ή όταν
 *    υπάρχουν κινήσεις στην περίοδο (για να φαίνονται/ακυρώνονται αφού έκλεισε) — withMovementsTab. Ποιος
 *    καταχωρεί/ακυρώνει το λέει ο server στο config (canCreate / canVoid).
 */

export type RegisterTab = 'summary' | 'movements' | 'staff' | 'actions' | 'deposits';

export interface RegisterAccess {
  /** «Εκτύπωση Χ» (και η «Σύνοψη»). */
  x: boolean;
  /** «Κλείσιμο ταμείου» (Ζ). */
  close: boolean;
  /** «Επανεκτύπωση τελευταίου Ζ». */
  reprint: boolean;
  /** Οι καρτέλες με τη σειρά τους — το παράθυρο ανοίγει στην πρώτη. */
  tabs: RegisterTab[];
  /** Υπάρχει η επιλογή «Ταμείο» στο μενού. */
  any: boolean;
}

type StoreLike = Pick<Store, 'dine_in'> | null | undefined;
type EmployeeLike = Pick<Employee, 'role' | 'permissions'> | null | undefined;

/** Ίδιο με το `isStoreAdmin` του Navigation. */
export const isStoreAdminRole = (role?: string | null) =>
  role === 'store_admin' || role === 'super_admin' || role === 'admin';

export function registerAccess(store: StoreLike, employee: EmployeeLike): RegisterAccess {
  const perms = employee?.permissions;
  const admin = isStoreAdminRole(employee?.role);
  const x = perms?.register_x_report !== false;
  const close = admin || employee?.role === 'manager' || perms?.close_register === true;
  const staff = admin || perms?.dine_in_view_registers !== false;
  const treats = store?.dine_in === true && x;

  const tabs: RegisterTab[] = [];
  if (x || close) tabs.push('summary');
  if (staff) tabs.push('staff');
  if (treats) tabs.push('actions');

  // Επανεκτύπωση: ο server τη δίνει μόνο με δικαίωμα Χ (canPullXReport) — όχι κουμπί που απαντά 403.
  return { x, close, reprint: x, tabs, any: tabs.length > 0 };
}

/** Η καρτέλα «Προκαταβολές», όταν ο διακόπτης του καταστήματος είναι ανοιχτός (useDepositsConfig). */
export function withDepositsTab(access: RegisterAccess, employee: EmployeeLike, depositsEnabled: boolean): RegisterAccess {
  const canCollect = isStoreAdminRole(employee?.role) || employee?.permissions?.dine_in_process_payment !== false;
  if (!depositsEnabled || !canCollect || access.tabs.includes('deposits')) return access;
  return { ...access, tabs: [...access.tabs, 'deposits'], any: true };
}

/** Η καρτέλα «Κινήσεις ταμείου», αμέσως μετά τη «Σύνοψη» (η συχνότερη ενέργεια του ταμία). */
export function withMovementsTab(access: RegisterAccess, visible: boolean): RegisterAccess {
  if (!visible || access.tabs.includes('movements')) return access;
  const at = access.tabs[0] === 'summary' ? 1 : 0;
  const tabs = [...access.tabs.slice(0, at), 'movements' as RegisterTab, ...access.tabs.slice(at)];
  return { ...access, tabs, any: true };
}
